import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToggles } from '../context/TogglesContext';
import { Modal } from '../components/common/Modal';
import { FileUpload } from '../components/common/FileUpload';
import { useT } from '../i18n';

type Page = 'dashboard' | 'resources' | 'capabilities' | 'outcomes' | 'inventory-resources' | 'inventory-capabilities' | 'inventory-outcomes' | 'linking' | 'link-qualification' | 'maturity-scoring' | 'weight-scoring' | 'maturity-qualification' | 'vrio' | 'admin';

interface DashboardPageProps {
  onNavigate: (page: Page) => void;
}

interface Stats {
  users: number;
  resources: number;
  capabilities: number;
  outcomes: number;
  links: number;
  criticalityRatings: number;
  vrioRatings: number;
  userProgress: {
    capabilitiesLinked: number;
    totalCapabilities: number;
  };
}

type UploadTarget = 'resources' | 'capabilities' | 'outcomes' | null;

const UPLOAD_CONFIG: Record<string, { title: string; headers: string[]; upload: (data: any[]) => Promise<any> }> = {
  resources: {
    title: 'Upload Resources CSV',
    headers: ['Title', 'Description', 'Type', 'Function'],
    upload: (data) => api.uploadResources(data),
  },
  capabilities: {
    title: 'Upload Capabilities CSV',
    headers: ['Title', 'Description', 'Nature', 'Domain'],
    upload: (data) => api.uploadCapabilities(data),
  },
  outcomes: {
    title: 'Upload Outcomes CSV',
    headers: ['Title', 'Description', 'Motivated By', 'Motivated For'],
    upload: (data) => api.uploadOutcomes(data),
  },
};

export function DashboardPage({ onNavigate }: DashboardPageProps) {
  const { t } = useT();
  const { isAdmin } = useAuth();
  const { toggles, isTabAccessible } = useToggles();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadTarget, setUploadTarget] = useState<UploadTarget>(null);

  const loadStats = () => {
    api.getStats()
      .then((data: any) => setStats(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleUpload = async (data: any[]) => {
    if (!uploadTarget) return;
    try {
      await UPLOAD_CONFIG[uploadTarget].upload(data);
      setUploadTarget(null);
      loadStats();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 200 }}>
        <div className="spinner" />
      </div>
    );
  }

  const phases = [
    {
      id: 'linking',
      title: t('dashboard.phase.linking.title'),
      description: t('dashboard.phase.linking.desc'),
      status: isTabAccessible('linking') ? (stats && stats.links > 0 ? 'active' : 'ready') : 'locked',
      detail: t('dashboard.capabilitiesLinked')
        .replace('{linked}', String(stats?.userProgress?.capabilitiesLinked || 0))
        .replace('{total}', String(stats?.userProgress?.totalCapabilities || 0)),
      action: () => onNavigate('linking'),
    },
    {
      id: 'link-qualification',
      title: t('dashboard.phase.linkQual.title'),
      description: t('dashboard.phase.linkQual.desc'),
      status: isTabAccessible('link-qualification') ? 'ready' : 'locked',
      detail: t('dashboard.phase.linkQual.detail'),
      action: () => onNavigate('link-qualification'),
    },
    {
      id: 'scoring',
      title: t('dashboard.phase.scoring.title'),
      description: t('dashboard.phase.scoring.desc'),
      status: isTabAccessible('maturity-scoring') ? (stats && stats.criticalityRatings > 0 ? 'active' : 'ready') : 'locked',
      detail: t('dashboard.ratingsCompleted').replace('{n}', String(stats?.criticalityRatings || 0)),
      action: () => onNavigate('weight-scoring'),
    },
    {
      id: 'maturity-qualification',
      title: t('dashboard.phase.maturityQual.title'),
      description: t('dashboard.phase.maturityQual.desc'),
      status: isTabAccessible('maturity-qualification') ? 'ready' : 'locked',
      detail: t('dashboard.phase.maturityQual.detail'),
      action: () => onNavigate('maturity-qualification'),
    },
  ];

  const setupItems = [
    { key: 'resources' as const, label: t('dashboard.stat.resources'), count: stats?.resources || 0, page: 'resources' as Page },
    { key: 'capabilities' as const, label: t('dashboard.stat.capabilities'), count: stats?.capabilities || 0, page: 'capabilities' as Page },
    { key: 'outcomes' as const, label: t('dashboard.stat.outcomes'), count: stats?.outcomes || 0, page: 'outcomes' as Page },
  ];

  return (
    <div>
      <div className="stats-grid mb-16">
        <div className="stat-card">
          <div className="stat-value">{stats?.resources || 0}</div>
          <div className="stat-label">{t('dashboard.stat.resources')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.capabilities || 0}</div>
          <div className="stat-label">{t('dashboard.stat.capabilities')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.outcomes || 0}</div>
          <div className="stat-label">{t('dashboard.stat.outcomes')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.users || 0}</div>
          <div className="stat-label">{t('dashboard.stat.users')}</div>
        </div>
      </div>

      {isAdmin && (
        <>
          <h3 className="mb-12">{t('dashboard.setup')}</h3>
          <div className="setup-grid mb-16">
            {setupItems.map((item) => (
              <div key={item.key} className="card setup-card" style={{ marginBottom: 0 }}>
                <div className="flex justify-between items-center mb-8">
                  <h4 style={{ margin: 0 }}>{item.label}</h4>
                  <span className="text-sm text-muted">{t('dashboard.itemCount').replace('{n}', String(item.count))}</span>
                </div>
                <div className="flex gap-8">
                  <button className="btn btn-accent btn-sm" onClick={() => onNavigate(item.page)}>
                    {t('dashboard.manage')}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setUploadTarget(item.key)}>
                    {t('csv.uploadCsv')}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => window.open(`/api/${item.key}/export-csv`, '_blank')}>
                    {t('dashboard.downloadCsv')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <h3 className="mb-12">{t('dashboard.assessmentPhases')}</h3>
      <div className="flex flex-col gap-12">
        {phases.map((phase) => {
          const statusColors: Record<string, string> = {
            ready: 'badge-ok',
            active: 'badge-accent',
            locked: 'badge-secondary',
          };

          const statusLabels: Record<string, string> = {
            ready: t('dashboard.status.open'),
            active: t('dashboard.status.active'),
            locked: t('dashboard.status.locked'),
          };

          return (
            <div key={phase.id} className="card" style={{ marginBottom: 0 }}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-8 mb-8">
                    <h4 style={{ margin: 0 }}>{phase.title}</h4>
                    <span className={`badge ${statusColors[phase.status]}`}>{statusLabels[phase.status]}</span>
                  </div>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>
                    {phase.description}
                  </p>
                  <p className="text-xs text-muted" style={{ marginTop: 4 }}>
                    {phase.detail}
                  </p>
                </div>
                <button
                  className="btn btn-accent"
                  onClick={phase.action}
                  disabled={phase.status === 'locked'}
                >
                  {phase.status === 'locked' ? t('dashboard.status.locked') : t('dashboard.status.open')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {uploadTarget && (
        <Modal title={t(`dashboard.uploadTitle.${uploadTarget}`)} onClose={() => setUploadTarget(null)}>
          <FileUpload
            expectedHeaders={UPLOAD_CONFIG[uploadTarget].headers}
            onUpload={handleUpload}
            onCancel={() => setUploadTarget(null)}
          />
        </Modal>
      )}
    </div>
  );
}
